// @givmo/passkeys — the escape hatch's ENTRY on a sign-in door (AS-WEB26): one quiet link, "Lost
// access to your company sign-in?", shown only while somebody here could be locked out by their
// company (D-AS-48 3a), and nothing at all anywhere else — a door with no lock behind it renders
// exactly as it did before.
//
// A link, never a button or a sentence: a credential screen doesn't argue its case. Each door gives
// it its own link look and alignment (the charity console's links are brand-700, the others' 600).
// While the lock's refusal notice is on screen the door leaves the link out: the notice carries the
// way back in itself, so one door, not two.
//
// WHETHER to show it is the backend's answer (`GET /authn/escape-hatch`, `readEscapeHatchEntry`), so
// there is no client flag to keep in step. Asked once per page load and kept per tab, like the passkey
// probe (`use-passkey-sign-in-availability.ts`): a module memo over `sessionStorage`, every storage
// access in a `try`. The verdicts keep for different times, and the asymmetry is the design:
//   - `shown`  30 min — a lock rarely ends within a sign-in visit, and a shown link costs a click;
//   - `hidden` 10 min — a lock can begin while a tab is open (an Owner's flip); the lock's refusal
//                       notice carries the way back in meanwhile, so the delay strands nobody;
//   - `unknown` 1 min — a 429, a 5xx, no answer, or none within `ENTRY_READ_TIMEOUT_MS`.
//
// ⚠ AN UNKNOWN ANSWER SHOWS THE LINK, and it is asked again once its minute has passed, within the
// page — never kept for the page's life. The backend's own read answers "a lock exists" when it
// fails, and for the same reason: a hidden way back in costs a locked-out person their account, a
// shown one costs a click (law 1). Before this, a failure here hid the link until a reload (cut
// 1007e's review, F1). So only a definite "no lock here" (`hidden`, or the dark 404) leaves the door
// as it was.

import { useEffect, useState } from 'react';
import { cn, testIdProp } from '@givmo/design-system';
import { ESCAPE_HATCH_ENTRY } from './escape-hatch-copy';
import { readEscapeHatchEntry, type EscapeHatchEntryVerdict } from './escape-hatch-transport';

/** Versioned so a later change to the stored shape cannot be misread as a valid old entry. */
const STORAGE_KEY = 'givmo.escape-hatch-entry.v1';

const TTL_MS: Record<EscapeHatchEntryVerdict, number> = {
  shown: 30 * 60 * 1000,
  hidden: 10 * 60 * 1000,
  unknown: 60 * 1000,
};

interface CachedVerdict {
  verdict: EscapeHatchEntryVerdict;
  expiresAt: number;
}

/**
 * How long the entry read may go unanswered before it counts as no answer (`unknown`, so the link
 * shows). Long enough for a cold backend to answer; the API client itself sets no timeout.
 */
export const ENTRY_READ_TIMEOUT_MS = 10_000;

/** The in-flight or settled read for this page load. */
interface Probe {
  promise: Promise<EscapeHatchEntryVerdict>;
  /** When an `unknown` verdict settled: such a read is asked again once its TTL has passed. */
  unknownSince: number | null;
}

let probe: Probe | null = null;

/**
 * Drop the memoised read, and by default the stored verdict too. A TEST SEAM: a suite that renders a
 * door must call it, or the first suite to settle the read decides the answer for every suite after.
 * `keepStoredVerdict: true` drops only the per-page-load memo, as a reload does.
 */
export function resetEscapeHatchEntryProbe(options: { keepStoredVerdict?: boolean } = {}): void {
  probe = null;
  if (options.keepStoredVerdict === true) return;
  try {
    globalThis.sessionStorage?.removeItem(STORAGE_KEY);
  } catch {
    // Storage is a convenience here, never a requirement.
  }
}

function readCachedVerdict(now: number): EscapeHatchEntryVerdict | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY);
    if (typeof raw !== 'string') return null;
    const parsed = JSON.parse(raw) as Partial<CachedVerdict>;
    if (typeof parsed.verdict !== 'string' || typeof parsed.expiresAt !== 'number') return null;
    if (!(parsed.verdict in TTL_MS)) return null;
    return parsed.expiresAt > now ? (parsed.verdict as EscapeHatchEntryVerdict) : null;
  } catch {
    return null;
  }
}

function writeCachedVerdict(verdict: EscapeHatchEntryVerdict, now: number): void {
  try {
    const entry: CachedVerdict = { verdict, expiresAt: now + TTL_MS[verdict] };
    globalThis.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Full quota or a blocked store — the in-memory memo still bounds this page load.
  }
}

/** The read, bounded: no answer within `ENTRY_READ_TIMEOUT_MS` is `unknown` (and the request ends). */
async function readBounded(): Promise<EscapeHatchEntryVerdict> {
  const controller = typeof AbortController === 'undefined' ? null : new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const unanswered = new Promise<EscapeHatchEntryVerdict>((resolve) => {
    timer = setTimeout(() => {
      controller?.abort();
      resolve('unknown');
    }, ENTRY_READ_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      readEscapeHatchEntry(controller === null ? undefined : { signal: controller.signal }),
      unanswered,
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

async function resolveVerdict(): Promise<EscapeHatchEntryVerdict> {
  const cached = readCachedVerdict(Date.now());
  if (cached !== null) return cached;
  const verdict = await readBounded();
  writeCachedVerdict(verdict, Date.now());
  return verdict;
}

/** This page load's read: the settled one, unless it said nothing and its minute has passed. */
function currentProbe(): Probe {
  if (
    probe !== null &&
    probe.unknownSince !== null &&
    Date.now() - probe.unknownSince >= TTL_MS.unknown
  ) {
    probe = null;
  }
  if (probe === null) {
    const started: Probe = { promise: resolveVerdict(), unknownSince: null };
    void started.promise.then((verdict) => {
      if (verdict === 'unknown') started.unknownSince = Date.now();
    });
    probe = started;
  }
  return probe;
}

/**
 * Whether a door shows the entry: `true` unless the backend has said no sign-in lock exists here
 * (`hidden`, or the dark 404) — an answer that says nothing shows it too (the header). Starts `false`
 * until the read settles, so a door with no lock behind it never draws the link at all; an unknown
 * answer is asked again once its minute has passed, while the door stays open.
 */
export function useEscapeHatchEntry(): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    let subscribed = true;
    let again: ReturnType<typeof setTimeout> | undefined;
    const ask = () => {
      void currentProbe().promise.then((verdict) => {
        if (!subscribed) return;
        setShown(verdict !== 'hidden');
        if (verdict === 'unknown') again = setTimeout(ask, TTL_MS.unknown);
      });
    };
    ask();
    return () => {
      subscribed = false;
      if (again !== undefined) clearTimeout(again);
    };
  }, []);
  return shown;
}

/** The charity console's link look (its "Forgot password?"): brand-700. */
export const ESCAPE_HATCH_LINK_CHARITY =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-700 outline-none transition-colors hover:text-brand-800 focus-visible:underline motion-reduce:transition-none';

/** The employer portal's and the company console's link look: brand-600. */
export const ESCAPE_HATCH_LINK_CONSOLE =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

export interface EscapeHatchEntryLinkProps {
  /** Opens the way back in (the console's `/lost-company-access`). */
  onOpen: () => void;
  /** The door's own link look. */
  className: string;
  /** Centred at the foot of a chooser; at the start of a page's own link list. */
  align?: 'center' | 'start';
}

/** The entry: the quiet link where a lock exists here, and nothing anywhere else. */
export function EscapeHatchEntryLink({
  onOpen,
  className,
  align = 'center',
}: EscapeHatchEntryLinkProps) {
  const shown = useEscapeHatchEntry();
  if (!shown) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(className, align === 'center' ? 'self-center' : 'self-start')}
      {...testIdProp('escape-hatch-entry')}
    >
      {ESCAPE_HATCH_ENTRY}
    </button>
  );
}
